import { Component, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '../pipes/translate.pipe';
import { TranslationService } from '../services/translation.service';
import { BrandService } from '../../services/brand-config.service';
import { loaderComponent } from '../shared/components/loader/loader.component';
import {
  DoctorReportService,
  DoctorReportData,
  BleedingDetail,
  Lifestyle,
  MedicationEntry,
  PriorHormoneTherapy,
  PeriodLast12,
  RatedSymptom,
  UntreatedCondition,
} from '../services/doctor-report.service';
import {
  WellnessTipsService,
  WellnessTipKey,
} from '../services/wellness-tips.service';
import MEDICATIONS_LIST from '../../assets/medications.json';

const REPORT_GENERATION_MIN_MS = 6000;

/** Nothing asked yet, which is what an untouched form means. */
function emptyPriorHormoneTherapy(): PriorHormoneTherapy {
  return { tried: null, types: [], stopReason: '' };
}

/** Same: null is "never asked", not "no". */
function emptyUntreatedCondition(): UntreatedCondition {
  return { has: null, detail: '' };
}

/**
 * Nothing answered yet. Every field starts null so an untouched question is
 * never submitted as a reported behaviour.
 */
function emptyLifestyle(): Lifestyle {
  return {
    alcohol: null,
    caffeine: null,
    exerciseTypes: [],
    exerciseOther: '',
    exerciseWeekly: null,
    exerciseChange: null,
    exerciseNote: '',
    sleepHours: null,
    sleepWaking: null,
    sleepChange: null,
    sleepNote: '',
    stress: null,
    smoking: null,
    smokingQuitYear: '',
    mealsHome: null,
    hotMeals: null,
  };
}

/** Nothing asked yet, which is what an untouched form means. */
function emptyBleedingDetail(): BleedingDetail {
  return {
    heavy: null,
    spotting: null,
    newOrUnexplained: null,
    postAny: null,
    postHeavy: null,
  };
}

@Component({
  selector: 'app-doctor-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    TranslatePipe,
    loaderComponent,
  ],
  templateUrl: './doctor-report.component.html',
  styleUrl: './doctor-report.component.scss',
})
export class DoctorReportComponent implements OnInit, OnDestroy {
  scanId: string = '';
  loading = true;
  /** B7: the form load / report generation failed -> loader shows Retry. */
  loadFailed = false;
  generateFailed = false;
  saving = false;
  generating = false;
  generationReady = false;
  /** 0-100. Drives the fill line on the "creating your report" screen. */
  generationProgress = 0;
  private progressTimer: any = null;
  generationTipKey: WellnessTipKey | null = null;
  reportId: number | null = null;
  brandName: string = '';

  // Auto-save state
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  private autoSaveTimer: any = null;
  private autoSaveDebounce: any = null;

  // Form data
  medications: MedicationEntry[] = [];
  /** Its own question, not a medication row: what she tried before, and why she stopped. */
  priorHormoneTherapy: PriorHormoneTherapy = emptyPriorHormoneTherapy();
  /** Something she knows about and deliberately leaves untreated. */
  untreatedCondition: UntreatedCondition = emptyUntreatedCondition();
  bleedingPatterns: string[] = [];
  bleedingNotes: string = '';
  visitGoal: string = '';
  /**
   * Three states per flag. null is the default and means she was never asked,
   * which is not the same as "no" -- the clinician report prints "not asked"
   * for it rather than a denial nobody made.
   */
  bleedingDetail: BleedingDetail = emptyBleedingDetail();
  /**
   * Her everyday pattern. Frequency questions only -- the food question asks how
   * many of her meals are prepared at home, never whether she cooks or orders
   * in, which reads as a judgement and gets answered with what sounds right.
   * Nothing here is scored or called healthy versus unhealthy; it is a pattern
   * the clinician can compare over time.
   */
  lifestyle: Lifestyle = emptyLifestyle();
  /** Her three priorities, in her order, as symptom keys. */
  concerns: (string | null)[] = [null, null, null];
  /** Everything she rated on this scan, highest first. */
  ratedSymptoms: RatedSymptom[] = [];

  /**
   * Her answer to "a period in the last 12 months" on the mapping's first
   * questionnaire, which is what decides how much of this step she is asked.
   * Stays null until the scan answers load, so the step renders in its full
   * form first and never flashes a narrower version at a woman it does not
   * apply to.
   */
  periodLast12: PeriodLast12 = null;

  /**
   * She had already saved a cycle-change pattern when this form opened. Taken
   * once, at load, so that skipping the list is decided before she touches it
   * and cannot flip while she is answering.
   */
  private hadSavedBleedingPatterns = false;
  /**
   * The rated-symptoms request has settled. Until it has, the step-4 block says
   * nothing: the empty-state line would otherwise flash on a scan she DID rate,
   * because the form renders before the request comes back.
   */
  ratedLoaded = false;
  /** Index of the slot whose "swap" list is open, or null. */
  concernPickerIndex: number | null = null;

  // UI state
  editingMedicationIndex: number | null = null;

  /** What she may have tried before. Multi-select: more than one is common. */
  priorHormoneTypes = ['pills', 'hormonal', 'other'];

  frequencyOptions = ['daily', 'weekly', 'nightly', 'as_needed', 'other'];

  consistencyOptions = [
    'very_consistently',
    'mostly_consistently',
    'sometimes_missed',
    'often_missed',
  ];

  startDateOptions = [
    'just_started',
    '1_6_months',
    'more_than_6_months',
    'dont_remember',
    'specific_date',
  ];

  /**
   * What changed about her cycle. "Periods have stopped" is deliberately NOT
   * here: it is a state rather than a change in pattern, and the mapping's
   * first questionnaire already asks it and uses it to set menopausal status.
   * Asking it twice with no rule for which answer wins would make this screen a
   * second source of truth for the most important fact in the product.
   *
   * "None of these" is not here either. It is exclusive, so it is rendered
   * apart from the list, below a divider, where it looks as exclusive as it
   * behaves.
   */
  bleedingOptions = [
    'heavier_bleeding',
    'lighter_bleeding',
    'closer_together',
    'farther_apart',
    'more_irregular',
  ];

  /* ---------- Step 3: everyday pattern ----------
     Each row is one question and a wrapping strip of choices, so seven
     questions read as a short list instead of seven stacked blocks. */

  alcoholOptions = [
    'none',
    'rarely',
    'monthly',
    'weekly_1_2',
    'weekly_3_5',
    'most_evenings',
    'daily',
  ];

  caffeineOptions = ['none', 'c1', 'c2', 'c3_4', 'c5_plus'];

  exerciseTypeOptions = [
    'walking',
    'yoga',
    'pilates',
    'strength',
    'cardio',
    'swimming',
    'dance',
    'other',
  ];

  exerciseWeeklyOptions = ['none', 'w1', 'w2', 'w3', 'w4_5', 'w6_plus'];

  changeOptions = ['same', 'more', 'less'];

  sleepHoursOptions = ['lt5', 'h5_6', 'h6_7', 'h7_8', 'gt8'];

  sleepWakingOptions = ['no', 'sometimes', 'most_nights', 'every_night'];

  sleepChangeOptions = ['same', 'worse', 'better'];

  stressOptions = ['low', 'low_moderate', 'moderate', 'moderate_high', 'high'];

  /** A level, not a yes/no -- "former, quit 2015" is a different fact to "never". */
  smokingOptions = [
    'never',
    'former',
    'occasional',
    'daily_lt10',
    'daily_10_20',
    'daily_20_plus',
  ];

  /** Her wording, kept exactly: most / about half / a minority / almost none. */
  mealsHomeOptions = ['most', 'about_half', 'few', 'almost_none'];

  hotMealsOptions = ['daily', 'most_days', 'few_times', 'rarely'];

  /**
   * Asked under the pattern list while she still bleeds. The list asks what
   * CHANGED; these ask what actually happened.
   */
  readonly bleedingFlagKeys: Array<keyof BleedingDetail> = [
    'heavy',
    'spotting',
    'newOrUnexplained',
  ];

  // Autocomplete state
  allMedications: string[] = MEDICATIONS_LIST;
  medicationsLower: string[] = MEDICATIONS_LIST.map(m => m.toLowerCase());
  filteredMedications: string[] = [];
  activeAutocomplete: { index: number } | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.autocomplete-wrapper')) {
      this.closeAutocomplete();
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private doctorReportService: DoctorReportService,
    private translationService: TranslationService,
    private brandService: BrandService,
    private wellnessTips: WellnessTipsService,
    private elementRef: ElementRef
  ) {
    this.brandName = this.brandService.config.brandName;
  }

  async ngOnInit() {
    this.scanId = this.route.snapshot.params['scanId'];
    this.scrollScreenToTop();
    await this.loadExistingReport();
    await this.loadRatedSymptoms();
    this.scrollScreenToTop();
    this.startAutoSave();
  }

  /**
   * Arriving from the results screen used to keep the results page scroll
   * offset, so the prep form opened in its middle instead of at its title
   * (Danna 2026-09-17). The app scrolls inside the shell's section rather than
   * on the window, so reset every scrollable ancestor as well as the page. Run
   * once on entry and again once the loaded report has grown the page.
   */
  private scrollScreenToTop(): void {
    if (typeof document === 'undefined') return;
    setTimeout(() => {
      let el: HTMLElement | null = this.elementRef?.nativeElement ?? null;
      while (el) {
        if (el.scrollTop > 0) el.scrollTop = 0;
        el = el.parentElement;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 0);
  }

  ngOnDestroy() {
    if (this.autoSaveTimer) clearInterval(this.autoSaveTimer);
    if (this.autoSaveDebounce) clearTimeout(this.autoSaveDebounce);
    this.stopGenerationProgress();
  }

  async loadExistingReport() {
    this.loading = true;
    this.loadFailed = false;
    try {
      const existing = await this.doctorReportService.getReportByScan(this.scanId);
      if (existing) {
        this.reportId = existing.id || null;
        this.applyReportDefaults(existing);
        return;
      }

      const latest = await this.doctorReportService.getLatestReport();
      if (latest) {
        this.applyReportDefaults(latest);
      }
    } catch (error) {
      console.error('Failed to load existing report:', error);
      // B7: surface the failure with Retry instead of dropping her into an
      // empty form as if nothing had gone wrong.
      this.loadFailed = true;
    } finally {
      this.loading = false;
      if (this.medications.length === 0) {
        this.addMedication();
      }
    }
  }

  private applyReportDefaults(report: DoctorReportData) {
    this.medications = [...(report.medications || [])];
    this.priorHormoneTherapy = {
      ...emptyPriorHormoneTherapy(),
      ...(report.priorHormoneTherapy || {}),
    };
    this.untreatedCondition = {
      ...emptyUntreatedCondition(),
      ...(report.untreatedCondition || {}),
    };
    this.bleedingPatterns = report.bleedingPatterns || [];
    this.hadSavedBleedingPatterns = this.bleedingPatterns.length > 0;
    this.bleedingNotes = report.bleedingNotes || '';
    this.visitGoal = report.visitGoal || '';
    this.bleedingDetail = {
      ...emptyBleedingDetail(),
      ...(report.bleedingDetail || {}),
    };
    this.lifestyle = {
      ...emptyLifestyle(),
      ...(report.lifestyle || {}),
    };
    // A reason saved against "more than before", back when that answer still
    // asked for one, has no field on screen any more. Drop it rather than let
    // it travel into the report where she cannot see or edit it.
    if (this.lifestyle.exerciseChange !== 'less') {
      this.lifestyle.exerciseNote = '';
    }
    const saved = (report.concerns || []).slice(0, 3);
    if (saved.length) {
      this.concerns = [saved[0] ?? null, saved[1] ?? null, saved[2] ?? null];
    }
  }

  /**
   * Pre-fills her three priorities with the three she rated highest. Only ever
   * fills empty slots, so re-opening the form never overwrites a swap she made.
   */
  private async loadRatedSymptoms() {
    if (!this.scanId) {
      this.ratedLoaded = true;
      return;
    }
    try {
      const answers = await this.doctorReportService.getRatedSymptoms(
        this.scanId,
      );
      this.ratedSymptoms = answers.rated;
      this.periodLast12 = answers.periodLast12;
    } finally {
      // Even a failed request has to settle, or the block waits forever.
      this.ratedLoaded = true;
    }
    const taken = this.concerns.filter(Boolean) as string[];
    const suggestions = this.ratedSymptoms
      .map((s) => s.key)
      .filter((key) => !taken.includes(key));
    for (let i = 0; i < this.concerns.length; i++) {
      if (this.concerns[i]) continue;
      const next = suggestions.shift();
      if (!next) break;
      this.concerns[i] = next;
    }
  }

  selectConsistency(index: number, value: string) {
    this.medications[index].consistency = value;
    this.triggerAutoSave();
  }

  /**
   * Same three-state answer as the bleeding rows: tapping the chosen button
   * again clears it back to "never asked". Answering anything other than yes
   * drops the follow-ups, so a detail she can no longer see is never submitted.
   */
  setPriorHormoneTried(value: boolean) {
    this.priorHormoneTherapy.tried =
      this.priorHormoneTherapy.tried === value ? null : value;
    if (this.priorHormoneTherapy.tried !== true) {
      this.priorHormoneTherapy.types = [];
      this.priorHormoneTherapy.stopReason = '';
    }
    this.triggerAutoSave();
  }

  togglePriorHormoneType(type: string) {
    const types = this.priorHormoneTherapy.types;
    const idx = types.indexOf(type);
    if (idx >= 0) types.splice(idx, 1);
    else types.push(type);
    this.triggerAutoSave();
  }

  isPriorHormoneType(type: string): boolean {
    return this.priorHormoneTherapy.types.includes(type);
  }

  setUntreatedCondition(value: boolean) {
    this.untreatedCondition.has =
      this.untreatedCondition.has === value ? null : value;
    if (this.untreatedCondition.has !== true) {
      this.untreatedCondition.detail = '';
    }
    this.triggerAutoSave();
  }

  toggleBleedingPattern(pattern: string) {
    const wasStopped = this.periodsStopped;
    const idx = this.bleedingPatterns.indexOf(pattern);
    if (pattern === 'no_change') {
      // "No change" cannot be true alongside a described change.
      this.bleedingPatterns = idx >= 0 ? [] : ['no_change'];
    } else if (idx >= 0) {
      this.bleedingPatterns.splice(idx, 1);
    } else {
      this.bleedingPatterns = this.bleedingPatterns
        .filter((p) => p !== 'no_change')
        .concat(pattern);
    }
    // Crossing between the two branches closes one set of questions and opens
    // another, so an answer given to one question must not sit in the payload
    // as if it answered the other. Adding a second pattern within the same
    // branch keeps what she already said.
    if (this.periodsStopped !== wasStopped) {
      this.bleedingDetail = emptyBleedingDetail();
    }
    this.triggerAutoSave();
  }

  isBleedingSelected(pattern: string): boolean {
    return this.bleedingPatterns.includes(pattern);
  }

  /** The exclusive "none of these" row, rendered on its own below the list. */
  get isNoneOfTheseSelected(): boolean {
    return this.bleedingPatterns.includes('no_change');
  }

  get isBleedingOtherSelected(): boolean {
    return this.bleedingPatterns.includes('other');
  }

  /**
   * She already told us on the mapping's first questionnaire that she has not
   * had a period in the last 12 months, so there is no current cycle whose
   * pattern could have changed. Only a clear "no" counts: unanswered and
   * "prefer not to say" both leave this false and she is asked in full.
   */
  get knownPeriodsStopped(): boolean {
    return this.periodLast12 === 'no';
  }

  /**
   * The list asks what changed about her cycle, so it is only worth asking of a
   * woman who has one. When we already know her periods stopped it is skipped
   * and she goes straight to the one question that still matters, the bleeding
   * since they stopped. A pattern she had already saved keeps the list on
   * screen even then, so nothing she wrote can become invisible and
   * uneditable.
   *
   * That second half reads the flag taken when the report loaded, not the live
   * array: read live, the list would disappear out from under her the moment
   * she cleared her last tick.
   */
  get showBleedingPatterns(): boolean {
    return !this.knownPeriodsStopped || this.hadSavedBleedingPatterns;
  }

  /**
   * Her periods have stopped, so the follow-ups change.
   *
   * The mapping's answer is the SINGLE source of truth now that this screen no
   * longer offers "periods have stopped" as a pattern: a clear yes means she
   * still has a cycle even if an old saved answer here says otherwise, and a
   * clear no means they stopped. The saved pattern is read only when the
   * mapping never got an answer, so reports written before this change still
   * open the right branch.
   */
  get periodsStopped(): boolean {
    if (this.periodLast12 === 'no') return true;
    if (this.periodLast12 === 'yes') return false;
    return this.bleedingPatterns.includes('periods_stopped');
  }

  /**
   * The follow-ups wait until she has said something about her pattern. Opening
   * the step with the list AND three more questions made it the longest card in
   * the form; this way it grows as she answers. An already-answered flag keeps
   * the block on screen even if she later clears the list, so a saved answer
   * can never become invisible and uneditable.
   */
  get showBleedingDetail(): boolean {
    // With the list gone there is nothing to answer first, so the question she
    // is here for opens straight away.
    if (this.knownPeriodsStopped) return true;
    if (this.bleedingPatterns.length > 0) return true;
    return Object.values(this.bleedingDetail).some((v) => v !== null);
  }

  /**
   * The follow-up rows actually on screen. Once her periods stopped there is no
   * "between periods" to ask about, and the severity question only makes sense
   * after she says there was bleeding at all.
   */
  get bleedingFlagRows(): Array<keyof BleedingDetail> {
    if (!this.periodsStopped) return this.bleedingFlagKeys;
    return this.bleedingDetail.postAny === true
      ? ['postAny', 'postHeavy']
      : ['postAny'];
  }

  bleedingFlag(key: keyof BleedingDetail): boolean | null {
    return this.bleedingDetail[key];
  }

  /** Tapping the answer already chosen puts the question back to "not asked". */
  setBleedingFlag(key: keyof BleedingDetail, value: boolean) {
    this.bleedingDetail[key] = this.bleedingDetail[key] === value ? null : value;
    if (key === 'postAny' && this.bleedingDetail.postAny !== true) {
      this.bleedingDetail.postHeavy = null;
    }
    this.triggerAutoSave();
  }

  // --- Step 3: everyday pattern -------------------------------------------

  /**
   * One choice per question. Tapping the choice she already made clears it back
   * to unanswered, the same three-state rule the rest of this form follows, so
   * a mis-tap is never stuck in her file as an answer.
   */
  setLifestyle(field: keyof Lifestyle, value: string) {
    const current = this.lifestyle[field];
    const next = current === value ? null : value;
    (this.lifestyle as any)[field] = next;

    // A follow-up she can no longer see must not travel with the payload.
    if (field === 'exerciseWeekly' && next === 'none') {
      this.lifestyle.exerciseTypes = [];
      this.lifestyle.exerciseOther = '';
      this.lifestyle.exerciseChange = null;
      this.lifestyle.exerciseNote = '';
    }
    if (field === 'exerciseChange' && next !== 'less') {
      this.lifestyle.exerciseNote = '';
    }
    if (field === 'sleepChange' && next !== 'worse' && next !== 'better') {
      this.lifestyle.sleepNote = '';
    }
    if (field === 'smoking' && next !== 'former') {
      this.lifestyle.smokingQuitYear = '';
    }
    this.triggerAutoSave();
  }

  isLifestyle(field: keyof Lifestyle, value: string): boolean {
    return this.lifestyle[field] === value;
  }

  toggleExerciseType(type: string) {
    const types = this.lifestyle.exerciseTypes;
    const idx = types.indexOf(type);
    if (idx >= 0) types.splice(idx, 1);
    else types.push(type);
    if (type === 'other' && idx >= 0) this.lifestyle.exerciseOther = '';
    this.triggerAutoSave();
  }

  isExerciseType(type: string): boolean {
    return this.lifestyle.exerciseTypes.includes(type);
  }

  /**
   * What she does and whether it changed wait until she has said how often,
   * the same way the bleeding follow-ups wait for the pattern list: the card
   * opens as a short list and grows as she answers. "Not at the moment" closes
   * it again, because there is no routine left to describe. An answer she has
   * already given keeps the rows on screen, so nothing saved becomes invisible
   * and uneditable.
   */
  get showExerciseDetail(): boolean {
    if (this.lifestyle.exerciseWeekly === 'none') return false;
    return (
      this.lifestyle.exerciseWeekly !== null ||
      this.lifestyle.exerciseTypes.length > 0 ||
      this.lifestyle.exerciseChange !== null
    );
  }

  /**
   * Only moving less has a reason worth asking about. Moving more is good news
   * that needs no explanation, and asking for one there reads like being
   * questioned about it.
   */
  get showExerciseNote(): boolean {
    return this.showExerciseDetail && this.lifestyle.exerciseChange === 'less';
  }

  get showSleepNote(): boolean {
    return (
      this.lifestyle.sleepChange === 'worse' ||
      this.lifestyle.sleepChange === 'better'
    );
  }

  /** She has told us something here, so the draft is worth saving on its own. */
  get lifestyleAnswered(): boolean {
    const l = this.lifestyle;
    return (
      l.exerciseTypes.length > 0 ||
      [
        l.alcohol,
        l.caffeine,
        l.exerciseWeekly,
        l.exerciseChange,
        l.sleepHours,
        l.sleepWaking,
        l.sleepChange,
        l.stress,
        l.smoking,
        l.mealsHome,
        l.hotMeals,
      ].some((v) => v !== null)
    );
  }

  // --- Step 4: the three she most wants to improve ------------------------

  concernLabelKey(key: string): string {
    return `doctorReport.symptoms.${key}`;
  }

  concernRating(key: string): number | null {
    const match = this.ratedSymptoms.find((s) => s.key === key);
    return match ? match.value : null;
  }

  toggleConcernPicker(index: number) {
    this.concernPickerIndex =
      this.concernPickerIndex === index ? null : index;
  }

  /**
   * What she can put in this slot: the rest of what she rated, highest first.
   * What already sits in another slot is gone, and so is what sits in this one
   * -- "swap" offers alternatives, not the choice she already made.
   */
  concernOptions(): RatedSymptom[] {
    const taken = this.concerns.filter(Boolean) as string[];
    return this.ratedSymptoms.filter((s) => !taken.includes(s.key));
  }

  pickConcern(index: number, key: string) {
    this.concerns[index] = key;
    this.concernPickerIndex = null;
    this.triggerAutoSave();
  }

  private scrollToEntry(selector: string, index: number) {
    setTimeout(() => {
      const entries = this.elementRef.nativeElement.querySelectorAll(selector);
      if (entries && entries[index]) {
        entries[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  addMedication() {
    this.medications.push({
      name: '',
      dose: '',
      frequency: 'daily',
      startDate: '',
      approximateStart: '',
      consistency: 'very_consistently',
      sideEffects: '',
      reason: '',
    });
    this.editingMedicationIndex = this.medications.length - 1;
    this.scrollToEntry('.medication-entry', this.medications.length - 1);
  }

  removeMedication(index: number) {
    this.medications.splice(index, 1);
    if (this.editingMedicationIndex === index) {
      this.editingMedicationIndex = null;
    } else if (this.editingMedicationIndex !== null && this.editingMedicationIndex > index) {
      this.editingMedicationIndex--;
    }
  }

  toggleEditMedication(index: number) {
    this.editingMedicationIndex =
      this.editingMedicationIndex === index ? null : index;
  }

  onMedicationInput(index: number, query: string) {
    if (query.length < 2) {
      this.closeAutocomplete();
      return;
    }
    const lower = query.toLowerCase();
    const results: string[] = [];
    for (let i = 0; i < this.medicationsLower.length && results.length < 8; i++) {
      if (this.medicationsLower[i].includes(lower)) {
        results.push(this.allMedications[i]);
      }
    }
    this.filteredMedications = results;
    this.activeAutocomplete = results.length > 0 ? { index } : null;
  }

  selectMedication(index: number, name: string) {
    this.medications[index].name = name;
    this.closeAutocomplete();
  }

  isAutocompleteOpen(index: number): boolean {
    return this.activeAutocomplete?.index === index;
  }

  closeAutocomplete() {
    this.activeAutocomplete = null;
    this.filteredMedications = [];
  }

  // Auto-save
  private startAutoSave() {
    this.autoSaveTimer = setInterval(() => this.autoSaveDraft(), 30000);
  }

  triggerAutoSave() {
    if (this.autoSaveDebounce) clearTimeout(this.autoSaveDebounce);
    this.autoSaveDebounce = setTimeout(() => this.autoSaveDraft(), 3000);
  }

  private async autoSaveDraft() {
    if (this.saving || this.loading) return;
    const data = this.getFormData();
    // Answering only the two questions below the medication list is a real
    // draft, so it has to survive on its own: the old guard saved nothing until
    // she also added a medication or a bleeding pattern.
    const answeredSomething =
      data.medications.length > 0 ||
      data.bleedingPatterns.length > 0 ||
      this.priorHormoneTherapy.tried !== null ||
      this.untreatedCondition.has !== null ||
      this.lifestyleAnswered;
    if (!answeredSomething) return;
    this.autoSaveStatus = 'saving';
    try {
      const saved = await this.doctorReportService.saveReport(data);
      if (saved?.id) this.reportId = saved.id;
      this.autoSaveStatus = 'saved';
    } catch {
      this.autoSaveStatus = 'error';
    }
  }

  // Datepicker helpers
  getStartDate(m: MedicationEntry): Date | null {
    return m.startDate ? new Date(m.startDate) : null;
  }

  onStartDateChange(m: MedicationEntry, date: Date | null) {
    m.startDate = date ? date.toISOString().split('T')[0] : '';
    this.triggerAutoSave();
  }

  getFormData(): DoctorReportData {
    return {
      id: this.reportId || undefined,
      scanId: this.scanId,
      medications: this.medications.filter((m) => m.name.trim() !== ''),
      priorHormoneTherapy: this.priorHormoneTherapy,
      untreatedCondition: this.untreatedCondition,
      bleedingPatterns: this.bleedingPatterns,
      bleedingDetail: this.bleedingDetail,
      bleedingNotes: this.bleedingNotes,
      lifestyle: this.lifestyle,
      visitGoal: this.visitGoal,
      concerns: this.concerns.filter(Boolean) as string[],
    };
  }

  async generateReport() {
    if (this.generating) return;
    this.generateFailed = false;
    this.saving = true;
    this.generating = true;
    this.generationReady = false;
    this.generationTipKey = this.wellnessTips.pickUnseen();
    this.wellnessTips.markSeen(this.generationTipKey);
    this.startGenerationProgress();

    const data = this.getFormData();
    const minDelay = new Promise((r) => setTimeout(r, REPORT_GENERATION_MIN_MS));
    try {
      const [saved] = await Promise.all([
        this.doctorReportService.saveReport(data),
        minDelay,
      ]);
      if (saved?.id) this.reportId = saved.id;
      this.stopGenerationProgress(100);
      this.generationReady = true;
    } catch (error) {
      console.error('Failed to generate report:', error);
      await minDelay;
      this.stopGenerationProgress();
      this.generating = false;
      // B7: everything she filled in stays in the form, so Retry just runs the
      // same save again.
      this.generateFailed = true;
    } finally {
      this.saving = false;
    }
  }

  /**
   * The fill line she watches while the report is built. It walks to 90% over the
   * minimum wait, then creeps to 97% so a slow server still looks alive, and only
   * reaches 100% when the report is actually ready (stopGenerationProgress(100)).
   */
  private startGenerationProgress() {
    this.stopGenerationProgress();
    this.generationProgress = 0;
    const stepMs = 100;
    const toNinety = 90 / (REPORT_GENERATION_MIN_MS / stepMs);
    this.progressTimer = setInterval(() => {
      if (this.generationProgress < 90) {
        this.generationProgress = Math.min(90, this.generationProgress + toNinety);
      } else if (this.generationProgress < 97) {
        this.generationProgress = Math.min(97, this.generationProgress + 0.05);
      }
    }, stepMs);
  }

  get generationProgressRounded(): number {
    return Math.round(this.generationProgress);
  }

  private stopGenerationProgress(final?: number) {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (final !== undefined) this.generationProgress = final;
  }

  viewGeneratedReport() {
    if (!this.generationReady) return;
    this.router.navigate(['/app/doctor-report', this.scanId, 'view']);
  }

  get generationTipTextKey(): string {
    return this.generationTipKey
      ? `wellnessTip.tips.${this.generationTipKey}`
      : '';
  }

  goBack() {
    this.router.navigate(['/app/results', this.scanId]);
  }

  getMedicationSummary(m: MedicationEntry): string {
    const freq = m.frequency === 'other' && m.frequencyCustom
      ? m.frequencyCustom
      : this.translationService.translate(`doctorReport.frequency.${m.frequency}`);
    const parts = [m.dose, freq];
    return parts.filter(Boolean).join(' \u00b7 ');
  }
}
